import React, { useEffect, useState, useCallback } from 'react';
import { Search, Check, X, Ban, RotateCcw, Copy, Key, ChevronDown, ExternalLink, FileText, AlertTriangle, CalendarClock, RefreshCw, History, KeyRound, Eye, Settings, Trash2, Clock, Phone, Mail, Link, CheckCheck, MoreVertical } from 'lucide-react';
import { adminApproveAccount, adminSuspendAccount, adminRestoreAccount, adminGenerateInviteCode, adminGetInviteCodes, adminUpdateGymPlan, adminGetPlanLogs, adminDeleteGym, adminRestoreGym, adminGetDeletedGyms, adminGetTrialRequests, adminUpdateTrialStatus, adminDeleteAccount } from './AdminPanel';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { format, parseISO, isPast, differenceInDays, addDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

const statusConfig = {
  pending: { label: '승인 대기', color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' },
  active: { label: '활성', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' },
  suspended: { label: '정지', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' },
};

const planConfig = {
  trial: { label: '무료체험', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' },
  active: { label: '이용중', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' },
  expired: { label: '만료', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' },
  suspended: { label: '정지', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700' },
};

function getPlanDday(expiresAt) {
  if (!expiresAt) return null;
  return differenceInDays(parseISO(expiresAt), new Date());
}

function DdayBadge({ expiresAt }) {
  const days = getPlanDday(expiresAt);
  if (days === null) return null;
  if (days < 0) return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
      만료됨
    </span>
  );
  if (days === 0) return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
      D-DAY
    </span>
  );
  if (days <= 7) return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
      D-{days}
    </span>
  );
  if (days <= 30) return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400">
      D-{days}
    </span>
  );
  return (
    <span className="text-xs text-gray-400 dark:text-gray-500">D-{days}</span>
  );
}

// 사업자 상세 정보 패널
function BusinessDetail({ acc }) {
  const fields = [
    { label: '대표자 연락처', value: acc.phone },
    { label: '사업장명', value: acc.business_name },
    { label: '매장 대표번호', value: acc.business_phone },
    { label: '사업장 주소', value: acc.business_address },
    { label: '사업자등록번호', value: acc.business_number },
    { label: '세금계산서 이메일', value: acc.tax_email },
  ].filter(f => f.value);

  if (fields.length === 0 && !acc.business_doc_url) {
    return <p className="text-xs text-gray-400 italic">제출된 사업장 정보가 없습니다.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {fields.map(f => (
          <div key={f.label}>
            <p className="text-xs text-gray-400">{f.label}</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{f.value}</p>
          </div>
        ))}
      </div>
      {acc.business_doc_url && (
        <button
          type="button"
          onClick={async () => {
            const { data, error } = await supabase.storage
              .from('business-docs')
              .createSignedUrl(acc.business_doc_url, 300);
            if (!error && data?.signedUrl) window.open(data.signedUrl, '_blank');
          }}
          className="inline-flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
        >
          <FileText className="w-3.5 h-3.5" />
          사업자등록증 확인
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

const ACTION_LABELS = {
  extend: { label: '기간 연장', color: 'text-green-600 dark:text-green-400' },
  reduce: { label: '기간 단축', color: 'text-orange-600 dark:text-orange-400' },
  plan_change: { label: '플랜 변경', color: 'text-blue-600 dark:text-blue-400' },
};

const PLAN_LABELS = { trial: '무료체험', active: '이용중', suspended: '정지' };

// 플랜 관리 패널
function PlanManager({ acc, onUpdate, onClose }) {
  const [plan, setPlan] = useState(acc.gym_plan || 'trial');
  const [expiresAt, setExpiresAt] = useState(
    acc.gym_plan_expires_at ? format(parseISO(acc.gym_plan_expires_at), 'yyyy-MM-dd') : ''
  );
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    if (!acc.gym_id) { setLogsLoading(false); return; }
    adminGetPlanLogs(acc.gym_id).then(data => {
      setLogs(data);
      setLogsLoading(false);
    });
  }, [acc.gym_id]);

  async function handleSave() {
    if (!acc.gym_id) return;
    if (!note.trim()) return;
    setSaving(true);
    const isoExpires = expiresAt ? new Date(expiresAt).toISOString() : null;
    const { error } = await adminUpdateGymPlan(acc.gym_id, plan, isoExpires, note.trim());
    setSaving(false);
    if (!error) {
      onUpdate(acc.id, plan, isoExpires);
      setNote('');
      adminGetPlanLogs(acc.gym_id).then(setLogs);
    }
  }

  function handleExtend(days) {
    const base = expiresAt && !isPast(new Date(expiresAt)) ? new Date(expiresAt) : new Date();
    setExpiresAt(format(addDays(base, days), 'yyyy-MM-dd'));
  }

  const inputCls = 'text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  const quickBtnCls = 'text-xs px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors';

  return (
    <div className="space-y-3">
      {/* 플랜 + 만료일 */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">플랜</label>
          <select value={plan} onChange={e => setPlan(e.target.value)} className={inputCls}>
            <option value="trial">무료체험</option>
            <option value="active">이용중</option>
            <option value="suspended">정지</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">만료일</label>
          <input
            type="date"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* 빠른 연장 / 단축 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-400">연장:</span>
        {[30, 90, 180, 365].map(d => (
          <button key={d} type="button" onClick={() => handleExtend(d)} className={quickBtnCls}>
            +{d >= 365 ? '1년' : d >= 180 ? '6개월' : d >= 90 ? '3개월' : '30일'}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-1">단축:</span>
        {[-7, -14, -30].map(d => (
          <button key={d} type="button" onClick={() => handleExtend(d)} className={`${quickBtnCls} text-orange-500 dark:text-orange-400 border-orange-200 dark:border-orange-800`}>
            {d}일
          </button>
        ))}
      </div>

      {/* 변경 사유 */}
      <div>
        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">변경 사유 <span className="text-red-500">*</span></label>
        <input
          type="text"
          className={`${inputCls} w-full`}
          placeholder="예) 결제 확인, 이벤트 적용, 환불 처리 등"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </div>

      {/* 버튼 */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={handleSave}
          disabled={saving || !note.trim()}
          className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {saving ? '저장 중...' : '저장'}
        </button>
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          닫기
        </button>
        <button
          onClick={() => setShowLogs(!showLogs)}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1 ml-auto"
        >
          <History className="w-3.5 h-3.5" />
          변경 이력 {logs.length > 0 ? `(${logs.length})` : ''}
        </button>
      </div>

      {/* 변경 이력 */}
      {showLogs && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">변경 이력 (최근 30건)</p>
          </div>
          {logsLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">이력이 없습니다.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {logs.map(log => {
                const cfg = ACTION_LABELS[log.action] || { label: log.action, color: 'text-gray-500' };
                return (
                  <div key={log.id} className="px-3 py-2.5 text-xs space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold ${cfg.color}`}>{cfg.label}</span>
                      {log.old_plan !== log.new_plan && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {PLAN_LABELS[log.old_plan] || log.old_plan} → {PLAN_LABELS[log.new_plan] || log.new_plan}
                        </span>
                      )}
                      {log.old_expires_at !== log.new_expires_at && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {log.old_expires_at || '-'} → {log.new_expires_at || '-'}
                        </span>
                      )}
                      <span className="ml-auto text-gray-400">
                        {format(parseISO(log.created_at), 'yy.MM.dd HH:mm', { locale: ko })}
                      </span>
                    </div>
                    {log.note && (
                      <p className="text-gray-500 dark:text-gray-400 pl-0.5">💬 {log.note}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 액션 버튼 ───────────────────────────────────────────
const actionBtnCls = 'flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-40 whitespace-nowrap';

function AccountActions({ acc, processing, openDropdownId, setOpenDropdownId, onApprove, onSuspendConfirm, onRestore, onPwResetConfirm, onViewGym, onManageConfirm, onDeleteGym, onPlanManage, onDeleteAccountConfirm }) {
  const isOpen = openDropdownId === acc.id;

  if (acc.status === 'pending') {
    return (
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => onApprove(acc.id)} disabled={!!processing} className={`${actionBtnCls} bg-green-600 text-white hover:bg-green-700`}>
          <Check className="w-3.5 h-3.5" /> 승인
        </button>
        <button onClick={() => onSuspendConfirm(acc.id, acc.name)} disabled={!!processing} className={`${actionBtnCls} bg-red-500 text-white hover:bg-red-600`}>
          <X className="w-3.5 h-3.5" /> 거절
        </button>
      </div>
    );
  }

  if (acc.status === 'suspended') {
    return (
      <button onClick={() => onRestore(acc.id)} disabled={!!processing} className={`${actionBtnCls} bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0`}>
        <RotateCcw className="w-3.5 h-3.5" /> 복구
      </button>
    );
  }

  if (acc.status === 'active') {
    return (
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setOpenDropdownId(isOpen ? null : acc.id)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)} />
            <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1">
              {acc.gym_id && (
                <>
                  <button
                    onClick={() => { setOpenDropdownId(null); onViewGym(acc.gym_id); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <Eye className="w-4 h-4 text-gray-400" /> 현황 보기
                  </button>
                  <button
                    onClick={() => { setOpenDropdownId(null); onPlanManage(acc.id); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <CalendarClock className="w-4 h-4 text-gray-400" /> 플랜 관리
                  </button>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                  <button
                    onClick={() => { setOpenDropdownId(null); onManageConfirm(acc.gym_id, acc.gym_name || acc.name); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  >
                    <Settings className="w-4 h-4" /> 대신 관리
                  </button>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                </>
              )}
              <button
                onClick={() => { setOpenDropdownId(null); onPwResetConfirm(acc.email); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <KeyRound className="w-4 h-4 text-gray-400" /> PW 재설정 메일
              </button>
              <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
              <button
                onClick={() => { setOpenDropdownId(null); onSuspendConfirm(acc.id, acc.name); }}
                disabled={!!processing}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
              >
                <Ban className="w-4 h-4" /> 계정 정지
              </button>
              {acc.gym_id ? (
                <button
                  onClick={() => { setOpenDropdownId(null); onDeleteGym(acc); }}
                  disabled={!!processing}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" /> 매장 삭제
                </button>
              ) : (
                <button
                  onClick={() => { setOpenDropdownId(null); onDeleteAccountConfirm(acc.id, acc.name); }}
                  disabled={!!processing}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" /> 계정 삭제
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

export default function AdminAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState(null);
  const [toast, setToast] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [planManageId, setPlanManageId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // 확인 모달
  const [suspendConfirm, setSuspendConfirm] = useState(null);      // { userId, name }
  const [manageConfirm, setManageConfirm] = useState(null);        // { gymId, gymName }
  const [pwResetConfirm, setPwResetConfirm] = useState(null);      // email string
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(null); // { userId, name }

  // 헬스장 삭제/복구
  const [deletedGyms, setDeletedGyms] = useState([]);
  const [deleteGymConfirm, setDeleteGymConfirm] = useState(null);
  const [deleteGymConfirmName, setDeleteGymConfirmName] = useState('');

  // 초대 코드
  const [inviteCodes, setInviteCodes] = useState([]);
  const [generatedCode, setGeneratedCode] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // 체험 신청
  const [trials, setTrials] = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(true);
  const [trialInviteLinks, setTrialInviteLinks] = useState({});
  const [trialApproveLoading, setTrialApproveLoading] = useState(null);
  const [trialCopiedId, setTrialCopiedId] = useState(null);
  const [trialDeleteConfirm, setTrialDeleteConfirm] = useState(null);
  const [trialDeleteLoading, setTrialDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    const { data, error } = await supabase.rpc('admin_get_accounts');
    if (error) { console.error('[adminGetAccounts]', error); setLoadError(true); setLoading(false); return; }
    setAccounts(data || []);
    setLoading(false);
  }, []);

  const loadInviteCodes = useCallback(async () => {
    const data = await adminGetInviteCodes();
    setInviteCodes(data || []);
  }, []);

  const loadDeletedGyms = useCallback(async () => {
    const data = await adminGetDeletedGyms();
    setDeletedGyms(data || []);
  }, []);

  const loadTrials = useCallback(async () => {
    setTrialsLoading(true);
    const data = await adminGetTrialRequests();
    setTrials(data || []);
    setTrialsLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadInviteCodes(); }, [loadInviteCodes]);
  useEffect(() => { loadDeletedGyms(); }, [loadDeletedGyms]);
  useEffect(() => { loadTrials(); }, [loadTrials]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }

  // ── 계정 액션 ──
  async function handleApprove(userId) {
    setProcessing(userId + '_approve');
    const { error } = await adminApproveAccount(userId);
    if (error) { showToast('승인 중 오류가 발생했습니다.'); setProcessing(null); return; }
    const expiresAt = addDays(new Date(), 14).toISOString();
    setAccounts(prev => prev.map(a =>
      a.id === userId ? { ...a, status: 'active', gym_plan: 'trial', gym_plan_expires_at: expiresAt } : a
    ));
    showToast('계정이 승인되었습니다. (무료체험 14일 자동 부여)');
    setProcessing(null);
  }

  async function handleSuspend(userId) {
    setSuspendConfirm(null);
    setProcessing(userId + '_suspend');
    const { error } = await adminSuspendAccount(userId);
    if (error) { showToast('정지 중 오류가 발생했습니다.'); setProcessing(null); return; }
    setAccounts(prev => prev.map(a => a.id === userId ? { ...a, status: 'suspended' } : a));
    showToast('계정이 정지되었습니다.');
    setProcessing(null);
  }

  async function handleDeleteAccount() {
    if (!deleteAccountConfirm) return;
    const { userId, name } = deleteAccountConfirm;
    setDeleteAccountConfirm(null);
    setProcessing(userId + '_deleteaccount');
    const { error } = await adminDeleteAccount(userId);
    if (error) { showToast('계정 삭제 실패: ' + error.message); setProcessing(null); return; }
    setAccounts(prev => prev.filter(a => a.id !== userId));
    showToast(`"${name}" 계정이 삭제되었습니다.`);
    setProcessing(null);
  }

  function handleManageGym(gymId) {
    setManageConfirm(null);
    navigate(`/admin/gyms/${gymId}/manage`);
  }

  async function handlePasswordReset(email) {
    setPwResetConfirm(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (!error) showToast(`${email}로 비밀번호 재설정 이메일을 발송했습니다.`);
    else showToast('이메일 발송에 실패했습니다.');
  }

  async function handleRestore(userId) {
    setProcessing(userId + '_restore');
    const { error } = await adminRestoreAccount(userId);
    if (error) { showToast('복구 중 오류가 발생했습니다.'); setProcessing(null); return; }
    setAccounts(prev => prev.map(a => a.id === userId ? { ...a, status: 'active' } : a));
    showToast('계정이 복구되었습니다.');
    setProcessing(null);
  }

  function handlePlanUpdate(userId, plan, expiresAt) {
    setAccounts(prev => prev.map(a =>
      a.id === userId ? { ...a, gym_plan: plan, gym_plan_expires_at: expiresAt } : a
    ));
    setPlanManageId(null);
    showToast('플랜이 업데이트되었습니다.');
  }

  async function handleDeleteGym() {
    if (!deleteGymConfirm) return;
    if (deleteGymConfirmName !== deleteGymConfirm.gym_name) return;
    setProcessing(deleteGymConfirm.gym_id + '_delete');
    const { error } = await adminDeleteGym(deleteGymConfirm.gym_id);
    if (!error) {
      showToast('헬스장이 삭제되었습니다. 7일 내 복구 가능합니다.');
      load();
      loadDeletedGyms();
    } else {
      showToast('삭제 실패: ' + error.message);
    }
    setProcessing(null);
    setDeleteGymConfirm(null);
    setDeleteGymConfirmName('');
  }

  async function handleRestoreGym(gymId, gymName) {
    setProcessing(gymId + '_restore');
    const { error } = await adminRestoreGym(gymId);
    if (!error) { showToast(`"${gymName}" 복구 완료`); load(); loadDeletedGyms(); }
    else showToast('복구 실패: ' + error.message);
    setProcessing(null);
  }

  async function handleGenerateCode() {
    setGenerating(true);
    const { code, error } = await adminGenerateInviteCode();
    setGenerating(false);
    if (error || !code) { showToast('초대 코드 생성에 실패했습니다.'); return; }
    const link = `${window.location.origin}/?code=${code}`;
    setGeneratedCode(code);
    setGeneratedLink(link);
    await loadInviteCodes();
  }

  async function handleCopy(text, type) {
    await navigator.clipboard.writeText(text);
    if (type === 'code') { setCopiedCode(true); setTimeout(() => setCopiedCode(false), 2000); }
    if (type === 'link') { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }
  }

  function getInviteStatus(ic) {
    if (ic.used_at) return { label: '사용됨', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700' };
    if (ic.expires_at && isPast(parseISO(ic.expires_at))) return { label: '만료', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' };
    return { label: '미사용', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' };
  }

  // ── 체험 신청 액션 ──
  async function handleTrialApprove(id) {
    setTrialApproveLoading(id);
    const { code, error: codeError } = await adminGenerateInviteCode();
    if (codeError || !code) { setTrialApproveLoading(null); showToast('초대코드 생성 실패'); return; }
    const { error: statusError } = await adminUpdateTrialStatus(id, 'contacted');
    if (statusError) { setTrialApproveLoading(null); showToast('상태 업데이트 실패'); return; }
    const url = `${window.location.origin}/?code=${code}`;
    setTrialInviteLinks(prev => ({ ...prev, [id]: url }));
    setTrials(prev => prev.map(r => r.id === id ? { ...r, status: 'contacted', processed_at: new Date().toISOString() } : r));
    setTrialApproveLoading(null);
    showToast('승인 완료 — 초대링크가 생성되었습니다.');
    await loadInviteCodes();
  }

  async function handleTrialReject(id) {
    const { error } = await adminUpdateTrialStatus(id, 'closed');
    if (!error) {
      setTrials(prev => prev.map(r => r.id === id ? { ...r, status: 'closed', processed_at: new Date().toISOString() } : r));
      showToast('체험 신청이 거절되었습니다.');
    } else {
      showToast('거절 처리 중 오류가 발생했습니다.');
    }
  }

  async function handleTrialDelete() {
    if (!trialDeleteConfirm) return;
    setTrialDeleteLoading(true);
    const { error } = await supabase.from('trial_requests').delete().eq('id', trialDeleteConfirm);
    if (!error) setTrials(prev => prev.filter(r => r.id !== trialDeleteConfirm));
    setTrialDeleteLoading(false);
    setTrialDeleteConfirm(null);
  }

  async function handleTrialCopy(id) {
    await navigator.clipboard.writeText(trialInviteLinks[id]);
    setTrialCopiedId(id);
    setTimeout(() => setTrialCopiedId(null), 2000);
  }

  // ── 필터링 ──
  const expiringAccounts = accounts.filter(a => {
    if (a.status !== 'active' || !a.gym_plan_expires_at) return false;
    const days = getPlanDday(a.gym_plan_expires_at);
    return days !== null && days <= 30;
  });

  const trialPendingCount = trials.filter(t => t.status === 'pending').length;

  const filtered = accounts
    .filter(a => {
      const matchSearch = !search || a.name?.includes(search) || a.email?.includes(search) || a.gym_name?.includes(search) || a.business_name?.includes(search);
      const matchFilter = filter === 'all' || a.status === filter ||
        (filter === 'trial' && a.status === 'active' && a.gym_plan === 'trial') ||
        (filter === 'expiring' && (() => {
          if (!a.gym_plan_expires_at) return false;
          const d = getPlanDday(a.gym_plan_expires_at);
          return d !== null && d <= 30;
        })());
      return matchSearch && matchFilter;
    })
    .sort((a, b) => {
      if (filter === 'expiring' || filter === 'active') {
        const da = getPlanDday(a.gym_plan_expires_at) ?? 9999;
        const db = getPlanDday(b.gym_plan_expires_at) ?? 9999;
        return da - db;
      }
      return 0;
    });

  const counts = {
    all: accounts.length,
    pending: accounts.filter(a => a.status === 'pending').length,
    trial: accounts.filter(a => a.status === 'active' && a.gym_plan === 'trial').length,
    active: accounts.filter(a => a.status === 'active').length,
    suspended: accounts.filter(a => a.status === 'suspended').length,
    expiring: expiringAccounts.length,
    trials: trials.length,
  };

  const isTrialTab = filter === 'trials';

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">계정 관리</h1>
        <button onClick={() => { load(); loadTrials(); }} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* 만료 임박 경고 배너 */}
      {expiringAccounts.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-700 dark:text-amber-400">
            <span className="font-medium">플랜 만료 임박 {expiringAccounts.length}건</span>
            <span className="text-amber-600 dark:text-amber-500 ml-2">
              {expiringAccounts.map(a => {
                const d = getPlanDday(a.gym_plan_expires_at);
                return `${a.gym_name || a.name}(D-${d})`;
              }).join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* 필터 탭 */}
      <div className="flex gap-2 flex-wrap">
        {[
          { val: 'all',      label: '전체',     activeClass: 'bg-gray-700 text-white',   warnClass: '' },
          { val: 'trials',   label: '체험 신청', activeClass: 'bg-purple-600 text-white', warnClass: 'text-purple-500 font-bold' },
          { val: 'pending',  label: '승인 대기', activeClass: 'bg-yellow-500 text-white', warnClass: 'text-yellow-500 font-bold' },
          { val: 'trial',    label: '무료체험',  activeClass: 'bg-blue-500 text-white',   warnClass: 'text-blue-500 font-bold' },
          { val: 'active',   label: '활성',     activeClass: 'bg-green-600 text-white',  warnClass: '' },
          { val: 'expiring', label: '만료 임박', activeClass: 'bg-amber-500 text-white',  warnClass: 'text-amber-500 font-bold' },
          { val: 'suspended',label: '정지',     activeClass: 'bg-red-600 text-white',    warnClass: '' },
        ].map(({ val, label, activeClass, warnClass }) => {
          const count = val === 'trials' ? trialPendingCount : counts[val];
          return (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === val
                  ? activeClass
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {label}
              {count > 0 && (
                <span className={`ml-1 ${filter !== val && warnClass ? warnClass : 'opacity-70'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 검색 */}
      {!isTrialTab && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="이름, 이메일, 헬스장명 검색..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* ── 체험 신청 탭 ── */}
      {isTrialTab ? (
        <div className="space-y-3">
          {trialsLoading ? (
            <div className="card flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : trials.length === 0 ? (
            <div className="card p-12 text-center text-gray-400">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">체험 신청 내역이 없습니다.</p>
            </div>
          ) : (
            trials.map(req => {
              const isPending = req.status === 'pending';
              const isContacted = req.status === 'contacted';
              const isClosed = req.status === 'closed' || req.status === 'converted';
              const trialStatusCfg = {
                pending: { label: '대기', color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' },
                contacted: { label: '승인됨', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' },
                converted: { label: '전환', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' },
                closed: { label: '거절', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700' },
              };
              const tCfg = trialStatusCfg[req.status] || trialStatusCfg.pending;

              return (
                <div key={req.id} className="card p-4">
                  <div className="flex items-start gap-3">
                    {/* 아바타 */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                      isPending ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {(req.name || '?')[0]}
                    </div>

                    {/* 정보 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-gray-900 dark:text-white text-sm">{req.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${tCfg.color}`}>{tCfg.label}</span>
                        {req.gym_name && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{req.gym_name}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-400">
                        {req.phone && (
                          <a href={`tel:${req.phone}`} className="flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400">
                            <Phone className="w-3 h-3" /> {req.phone}
                          </a>
                        )}
                        {req.email && (
                          <a href={`mailto:${req.email}`} className="flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400">
                            <Mail className="w-3 h-3" /> {req.email}
                          </a>
                        )}
                        {req.created_at && (
                          <span>{format(parseISO(req.created_at), 'yy.MM.dd HH:mm', { locale: ko })}</span>
                        )}
                      </div>
                      {req.note && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-2 py-1 bg-gray-50 dark:bg-gray-700 rounded">
                          {req.note}
                        </p>
                      )}
                      {/* 초대링크 표시 */}
                      {trialInviteLinks[req.id] && (
                        <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <span className="text-xs text-blue-700 dark:text-blue-300 flex-1 truncate">{trialInviteLinks[req.id]}</span>
                          <button
                            onClick={() => handleTrialCopy(req.id)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-shrink-0"
                          >
                            {trialCopiedId === req.id ? <><CheckCheck className="w-3 h-3" /> 복사됨</> : <><Copy className="w-3 h-3" /> 복사</>}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isPending && (
                        <>
                          <button
                            onClick={() => handleTrialApprove(req.id)}
                            disabled={trialApproveLoading === req.id}
                            className={`${actionBtnCls} bg-blue-600 text-white hover:bg-blue-700`}
                          >
                            <Link className="w-3.5 h-3.5" />
                            {trialApproveLoading === req.id ? '처리중...' : '승인'}
                          </button>
                          <button onClick={() => handleTrialReject(req.id)} className={`${actionBtnCls} text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`}>
                            <X className="w-3.5 h-3.5" /> 거절
                          </button>
                        </>
                      )}
                      {isContacted && !trialInviteLinks[req.id] && (
                        <button
                          onClick={() => handleTrialApprove(req.id)}
                          disabled={trialApproveLoading === req.id}
                          className={`${actionBtnCls} border border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20`}
                        >
                          <Link className="w-3.5 h-3.5" /> 링크 재발급
                        </button>
                      )}
                      {isClosed && (
                        <span className="text-xs text-gray-400">처리 완료</span>
                      )}
                      <button
                        onClick={() => setTrialDeleteConfirm(req.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* ── 계정 목록 ── */
        <div className="card">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : loadError ? (
            <div className="text-center py-16">
              <p className="text-red-500 font-medium mb-1">데이터를 불러오지 못했습니다.</p>
              <p className="text-xs text-gray-400 mb-4">권한이 없거나 서버 오류가 발생했습니다.</p>
              <button onClick={load} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">다시 시도</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">계정이 없습니다.</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map(acc => {
                const sCfg = statusConfig[acc.status] || statusConfig.active;
                const pCfg = planConfig[acc.gym_plan] || { label: acc.gym_plan || '-', color: 'text-gray-500' };
                const isExpanded = expandedId === acc.id;
                const isPlanManaging = planManageId === acc.id;
                const hasBizInfo = acc.phone || acc.business_name || acc.business_address || acc.business_doc_url;
                const ddays = getPlanDday(acc.gym_plan_expires_at);
                const isExpiringSoon = ddays !== null && ddays <= 7;

                const avatarStyle = acc.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : acc.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';

                return (
                  <div key={acc.id} className={`transition-colors ${isExpiringSoon ? 'border-l-2 border-amber-400' : ''}`}>
                    <div className="px-4 py-3.5">
                      <div className="flex items-start gap-3">
                        {/* 아바타 */}
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm mt-0.5 ${avatarStyle}`}>
                          {(acc.name || '?')[0]}
                        </div>

                        {/* 메인 정보 */}
                        <div className="flex-1 min-w-0">
                          {/* 1행: 이름 + 뱃지들 */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-gray-900 dark:text-white text-sm">{acc.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${sCfg.color}`}>
                              {sCfg.label}
                            </span>
                            {acc.gym_name && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${pCfg.color}`}>
                                {pCfg.label}
                              </span>
                            )}
                            <DdayBadge expiresAt={acc.gym_plan_expires_at} />
                            {acc.status === 'pending' && acc.business_doc_url && (
                              <span className="text-xs px-1.5 py-0.5 rounded-md font-medium bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                서류첨부
                              </span>
                            )}
                          </div>

                          {/* 2행: 연락처 · 이메일 · 가입일 */}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                            {acc.phone && (
                              <span className="text-gray-500 dark:text-gray-400 mr-2">{acc.phone}</span>
                            )}
                            {acc.email}
                            {acc.created_at && (
                              <span className="ml-2 text-gray-300 dark:text-gray-600">
                                · {format(parseISO(acc.created_at), 'yy.MM.dd', { locale: ko })} 가입
                              </span>
                            )}
                          </p>

                          {/* 3행: 헬스장 정보 */}
                          {acc.gym_name ? (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{acc.gym_name}</span>
                              {acc.gym_code && (
                                <span className="text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                  {acc.gym_code}
                                </span>
                              )}
                              {acc.gym_plan_expires_at && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  ~{format(parseISO(acc.gym_plan_expires_at), 'yy.MM.dd')}
                                </span>
                              )}
                              {acc.business_name && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">· {acc.business_name}</span>
                              )}
                              {hasBizInfo && (
                                <button
                                  onClick={() => setExpandedId(isExpanded ? null : acc.id)}
                                  className="text-xs text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-0.5 ml-1"
                                >
                                  <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mt-1">
                              {acc.role === 'gym_owner' && !acc.gym_id && acc.status !== 'pending' && (
                                <span className="text-xs px-1.5 py-0.5 rounded-md bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 font-medium">매장 미등록</span>
                              )}
                              {acc.role === 'gym_owner' && acc.gym_id && !acc.gym_name && acc.status !== 'pending' && (
                                <span className="text-xs px-1.5 py-0.5 rounded-md bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 font-medium">매장 삭제됨</span>
                              )}
                              {acc.business_name && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">{acc.business_name}</span>
                              )}
                              {hasBizInfo && (
                                <button
                                  onClick={() => setExpandedId(isExpanded ? null : acc.id)}
                                  className="text-xs text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-0.5"
                                >
                                  상세정보
                                  <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* 액션 버튼 (우측 정렬) */}
                        <AccountActions
                          acc={acc}
                          processing={processing}
                          openDropdownId={openDropdownId}
                          setOpenDropdownId={setOpenDropdownId}
                          onApprove={handleApprove}
                          onSuspendConfirm={(userId, name) => setSuspendConfirm({ userId, name })}
                          onRestore={handleRestore}
                          onPwResetConfirm={email => setPwResetConfirm(email)}
                          onViewGym={id => navigate(`/admin/gyms/${id}`)}
                          onManageConfirm={(gymId, gymName) => setManageConfirm({ gymId, gymName })}
                          onDeleteGym={a => { setDeleteGymConfirm(a); setDeleteGymConfirmName(''); }}
                          onPlanManage={id => setPlanManageId(isPlanManaging ? null : id)}
                          onDeleteAccountConfirm={(userId, name) => setDeleteAccountConfirm({ userId, name })}
                        />
                      </div>
                    </div>

                    {/* 사업장 상세 */}
                    {isExpanded && (
                      <div className="px-5 pb-4 border-t border-gray-50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50 pt-3">
                        <BusinessDetail acc={acc} />
                      </div>
                    )}

                    {/* 플랜 관리 */}
                    {isPlanManaging && (
                      <div className="px-5 pb-4 border-t border-amber-100 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-900/10 pt-3">
                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">플랜 관리</p>
                        <PlanManager
                          acc={acc}
                          onUpdate={handlePlanUpdate}
                          onClose={() => setPlanManageId(null)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 초대 링크 발급 — 계정 탭일 때만 표시 */}
      {!isTrialTab && (
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white text-sm">대표자 초대 링크 발급</h2>
                <p className="text-xs text-gray-400 mt-0.5">링크를 전달하면 대표자가 클릭 후 바로 가입할 수 있습니다.</p>
              </div>
            </div>
            <button
              onClick={handleGenerateCode}
              disabled={generating}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex-shrink-0"
            >
              <Key className="w-3.5 h-3.5" />
              {generating ? '생성 중...' : '초대 링크 발급'}
            </button>
          </div>

          {generatedCode && (
            <div className="space-y-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-1">가입 링크 (클릭하면 코드 자동 입력)</p>
                  <p className="text-xs font-mono text-primary-700 dark:text-primary-300 truncate bg-white dark:bg-gray-800 rounded px-2 py-1">
                    {generatedLink}
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(generatedLink, 'link')}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors flex-shrink-0"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedLink ? '복사됨' : '링크 복사'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-1">초대 코드 (직접 전달용)</p>
                  <span className="font-mono text-lg font-bold tracking-widest text-primary-700 dark:text-primary-300">
                    {generatedCode}
                  </span>
                </div>
                <button
                  onClick={() => handleCopy(generatedCode, 'code')}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors flex-shrink-0"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedCode ? '복사됨' : '코드 복사'}
                </button>
              </div>
              <p className="text-xs text-primary-500 dark:text-primary-500">
                코드는 7일간 유효하며 1회만 사용 가능합니다.
              </p>
            </div>
          )}

          {inviteCodes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">발급된 초대 코드가 없습니다.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              {inviteCodes.map(ic => {
                const inviteStatus = getInviteStatus(ic);
                return (
                  <div key={ic.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                    <span className="font-mono font-bold text-gray-800 dark:text-gray-200 tracking-widest w-24 flex-shrink-0">
                      {ic.code}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${inviteStatus.color}`}>
                      {inviteStatus.label}
                    </span>
                    <div className="flex-1 min-w-0 text-xs text-gray-400 dark:text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>발급: {ic.created_at ? format(parseISO(ic.created_at), 'yyyy.MM.dd', { locale: ko }) : '-'}</span>
                      <span>만료: {ic.expires_at ? format(parseISO(ic.expires_at), 'yyyy.MM.dd', { locale: ko }) : '-'}</span>
                      {ic.used_by_email && <span className="truncate">사용자: {ic.used_by_email}</span>}
                    </div>
                    {!ic.used_at && !(ic.expires_at && isPast(parseISO(ic.expires_at))) && (
                      <button
                        onClick={() => handleCopy(`${window.location.origin}/login?code=${ic.code}`, 'link')}
                        className="text-xs text-gray-400 hover:text-primary-600 flex-shrink-0"
                        title="링크 복사"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 삭제된 헬스장 복구 */}
      {!isTrialTab && deletedGyms.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> 삭제된 헬스장 (7일 내 복구 가능)
          </h2>
          <div className="card overflow-hidden">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {deletedGyms.map(gym => (
                <div key={gym.id} className="px-5 py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-500 dark:text-gray-400 text-sm line-through">{gym.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{gym.owner_name} ({gym.owner_email})</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-orange-500 font-medium">{gym.expires_in_days}일 후 영구삭제</span>
                    <button
                      onClick={() => handleRestoreGym(gym.id, gym.name)}
                      disabled={processing === gym.id + '_restore'}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> 복구
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 헬스장 삭제 확인 모달 */}
      {deleteGymConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="w-5 h-5 text-red-500" />
              <h3 className="font-bold text-gray-900 dark:text-white">헬스장 삭제</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              삭제 후 7일 내 복구 가능합니다. 계속하려면 헬스장명을 입력하세요.
            </p>
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400 text-center">{deleteGymConfirm.gym_name}</p>
            </div>
            <input
              className="input mb-4"
              placeholder="헬스장명 입력"
              value={deleteGymConfirmName}
              onChange={e => setDeleteGymConfirmName(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={handleDeleteGym}
                disabled={deleteGymConfirmName !== deleteGymConfirm.gym_name || !!processing}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40"
              >
                삭제
              </button>
              <button
                onClick={() => { setDeleteGymConfirm(null); setDeleteGymConfirmName(''); }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 계정 정지 확인 모달 */}
      <ConfirmDialog
        isOpen={!!suspendConfirm}
        title="계정 정지"
        message={`${suspendConfirm?.name} 계정을 정지하시겠습니까? 해당 대표자는 서비스에 접근할 수 없게 됩니다.`}
        confirmText="정지"
        confirmClass="btn-danger"
        onConfirm={() => handleSuspend(suspendConfirm.userId)}
        onCancel={() => setSuspendConfirm(null)}
      />

      {/* 대신 관리 확인 모달 */}
      <ConfirmDialog
        isOpen={!!manageConfirm}
        title="대신 관리 진입"
        message={`"${manageConfirm?.gymName}" 매장을 대신 관리하는 모드로 진입합니다. 실제 데이터가 변경될 수 있습니다.`}
        confirmText="진입"
        confirmClass="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
        onConfirm={() => handleManageGym(manageConfirm.gymId)}
        onCancel={() => setManageConfirm(null)}
      />

      {/* PW 재설정 확인 모달 */}
      <ConfirmDialog
        isOpen={!!pwResetConfirm}
        title="비밀번호 재설정 메일 발송"
        message={`${pwResetConfirm} 으로 비밀번호 재설정 이메일을 발송하시겠습니까?`}
        confirmText="발송"
        confirmClass="px-4 py-2 text-sm font-medium rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors"
        onConfirm={() => handlePasswordReset(pwResetConfirm)}
        onCancel={() => setPwResetConfirm(null)}
      />

      {/* 계정 삭제 확인 모달 (매장 미등록) */}
      <ConfirmDialog
        isOpen={!!deleteAccountConfirm}
        title="계정 삭제"
        message={`"${deleteAccountConfirm?.name}" 계정을 완전히 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        confirmClass="btn-danger"
        onConfirm={handleDeleteAccount}
        onCancel={() => setDeleteAccountConfirm(null)}
      />

      {/* 체험 신청 삭제 확인 모달 */}
      {trialDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-xs shadow-xl">
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">정말 삭제하시겠습니까?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">삭제된 신청 내역은 복구할 수 없습니다.</p>
            <div className="flex gap-2">
              <button onClick={handleTrialDelete} disabled={trialDeleteLoading} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
                {trialDeleteLoading ? '삭제 중...' : '삭제'}
              </button>
              <button onClick={() => setTrialDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-xl shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
